import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { FormFields } from '~/components/FormFields'
import { StateEl } from '../style'
import { GlobalEl } from '~/styles/app'
import DairyQuery from '~/QueryHooks/DairyQuery'
import notify from '~/components/Notify'
import Drawer from '~/components/Drawer'
import { KeyLabelType } from '~/types'
import { DrawerMilkPriceType } from '.'

interface Props {
  drawerData: DrawerMilkPriceType
  handleHiddenDrawer: () => void
  months: KeyLabelType[]
  fetchData: () => void
}

const CreateEditMilkPrice: React.FC<Props> = ({ drawerData, handleHiddenDrawer, months, fetchData }) => {
  const { t } = useTranslation()
  const { handleSubmit, control, reset } = useForm({ mode: 'onSubmit' })
  const { isVisible, idMilkPrice, milkTypeId } = drawerData

  const { data: editData } = DairyQuery.editSegmentationPrice(idMilkPrice ? String(idMilkPrice) : '')

  useEffect(() => {
    if (isVisible) {
      if (editData) {
        reset({
          month: editData.month,
          basePrice: editData.basePrice,
          planningBonus: editData.planningBonus,
          ulDeduction: editData.ulDeduction,
          selectionBonus: editData.selectionBonus,
          priceSet: editData?.priceSet || 0
        })
        return
      }

      reset({
        month: '',
        basePrice: '',
        planningBonus: '',
        ulDeduction: '',
        selectionBonus: ''
      })
    }
  }, [isVisible, editData])

  const isEditMode = Boolean(drawerData.idMilkPrice)

  const onSuccess = () => {
    notify(isEditMode ? t('Successfully updated milk price') : t('Successfully created milk price'), 'success')
    fetchData()
    handleHiddenDrawer()
  }

  const { mutate: storePrice, isLoading: isStoring } = DairyQuery.storeSegmentationPrice(onSuccess)
  const { mutate: updatePrice, isLoading: isUpdating } = DairyQuery.updateSegmentationPrice(onSuccess)
  const isLoading = isStoring || isUpdating

  const onSubmit = (data) => {
    if (isEditMode) {
      updatePrice({ id: drawerData.idMilkPrice, data: { ...data, milkTypeId } })
      return
    }

    storePrice({ ...data, milkTypeId })
  }

  const monthOptions = months.map(m => ({ key: m.key, value: m.key, text: m.label }))

  const renderContent = () => (
    <>
      <FormFields.Select
        control={control}
        name='month'
        disabled={isEditMode}
        label={t('Month')}
        options={monthOptions}
        placeholder={t('Select month')}
        rules={{ required: { value: t('Please select month'), message: t('Month is required') } }}
      />
      <FormFields.Input
        control={control}
        name='basePrice'
        label={t('Base Price')}
        placeholder={t('Base Price')}
        typeInput='controled'
        allowDecimals
        decimalScale={2}
        rules={{
          required: t('Please senter base price'),
          validate: (value) => {
            const transformedValue = value?.toString()?.replace(',', '.')
            if (transformedValue < 10 || transformedValue > 200) {
              return t('The base price must be between 10.00 and 200.00.')
            }
            return
          }
        }}
      />
      <FormFields.Input
        control={control}
        name='planningBonus'
        label={t('Planning Bonus')}
        placeholder={t('Planning Bonus')}
        typeInput='controled'
        rules={{
          required: t('Please enter Planning Bonus'),
          validate: (value) => {
            const transformedValue = value?.toString()?.replace(',', '.')
            if (transformedValue < 0 || transformedValue > 100) {
              return t('The planing bonus must be between 0 and 100.00.')
            }
            return
          }
        }}
        allowDecimals
        decimalScale={2}
      />
      <FormFields.Input
        control={control}
        name='ulDeduction'
        label={t('UL Deduction')}
        rules={{
          required: t('Please enter UL Deduction'),
          min: {
            value: -50,
            message: t('Value must be at least -50')
          },
          max: {
            value: 0,
            message: t('Value must be at most 0')
          }
        }}
        placeholder={t('UL Deduction')}
        typeInput='controled'
      />
      <FormFields.Input
        control={control}
        name='selectionBonus'
        rules={{
          required: t('Please enter Selection Bonus'),
          validate: (value) => {
            const transformedValue = value?.toString()?.replace(',', '.')
            if (transformedValue < 0 || transformedValue > 50) {
              return t('The selection bonus must be between 0 and 50.')
            }
            return
          }
        }}
        label={t('Selection Bonus')}
        placeholder={t('Selection Bonus')}
        typeInput='controled'
      />
      <FormFields.Checkbox
        control={control}
        name='priceSet'
        defaultValue={0}
        checkboxValue={1}
        label={t('Milk Price Fixed')}
      />
      <StateEl.DrawerFooter>
        <GlobalEl.PrimaryButton disabled={isLoading}>
          {isEditMode ? t('Update') : t('Add')}
        </GlobalEl.PrimaryButton>
      </StateEl.DrawerFooter>
    </>
  )

  return <Drawer
    isVisible={isVisible}
    handleSubmit={handleSubmit(onSubmit)}
    isBusy={isLoading}
    header={isEditMode ? t('Edit milk price') : t('Add milk price')}
    closeOnEscape
    onHide={() => {
      reset()
      handleHiddenDrawer()
    }}
    content={renderContent()}
  />
}

export default CreateEditMilkPrice
